import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import JsBarcode from 'jsbarcode';
import {
  isFreeTicket,
  NfcAssociateEvent,
  ParticipantDetail,
  ParticipantHistoryEntry,
  TplChangeEvent,
  TplOption,
} from './participant-detail-drawer.types';

type StatusKind = 'ok' | 'wait' | 'check' | 'block';

interface StatusPill {
  cls: string;
  icon: string;
  label: string;
}

const STATUS_PILLS: Record<StatusKind, StatusPill> = {
  ok:    { cls: 'pill--ok',    icon: 'fa-circle',       label: 'Valide' },
  wait:  { cls: 'pill--wait',  icon: 'fa-clock',        label: 'Non composté' },
  check: { cls: 'pill--check', icon: 'fa-circle-check', label: 'Contrôlé' },
  block: { cls: 'pill--block', icon: 'fa-ban',          label: 'Bloqué' },
};

@Component({
  selector: 'app-participant-detail-drawer',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './participant-detail-drawer.component.html',
  styleUrls: ['./participant-detail-drawer.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParticipantDetailDrawerComponent implements OnChanges, AfterViewInit, OnDestroy {
  constructor(private cdr: ChangeDetectorRef) {}
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnDestroy(): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }
  }

  @Input() isOpen = false;
  @Input() participant: ParticipantDetail | null = null;
  @Input() availableTpls: TplOption[] = [];

  @ViewChild('barcodeSvg') barcodeSvg?: ElementRef<SVGSVGElement>;

  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<{ id: string; lastName: string; firstName: string }>();
  @Output() tplChanged = new EventEmitter<TplChangeEvent>();
  @Output() nfcAssociated = new EventEmitter<NfcAssociateEvent>();
  @Output() nfcDisassociated = new EventEmitter<{ participantId: string }>();
  @Output() flagToggled = new EventEmitter<{ participantId: string; flag: boolean }>();
  @Output() banToggled = new EventEmitter<{ participantId: string; ban: boolean }>();
  @Output() scanIncremented = new EventEmitter<{ participantId: string }>();
  @Output() scanDecremented = new EventEmitter<{ participantId: string }>();

  lastNameDraft = '';
  firstNameDraft = '';
  nfcDraft = '';
  tplDropdownOpen = false;
  tplFilter = '';
  toast: { msg: string; icon: string } | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['participant'] && this.participant) {
      this.lastNameDraft = this.participant.lastName;
      this.firstNameDraft = this.participant.firstName;
      this.nfcDraft = '';
      this.tplDropdownOpen = false;
      queueMicrotask(() => this.renderBarcode());
    }
  }

  ngAfterViewInit(): void {
    this.renderBarcode();
  }

  private renderBarcode(): void {
    const svg = this.barcodeSvg?.nativeElement;
    const code = this.participant?.barcode;
    if (!svg || !code) return;
    JsBarcode(svg, code, {
      format: 'CODE128',
      width: 1.5,
      height: 40,
      displayValue: false,
      margin: 0,
    });
  }

  get isDirty(): boolean {
    if (!this.participant) return false;
    return this.lastNameDraft !== this.participant.lastName
        || this.firstNameDraft !== this.participant.firstName;
  }

  get isBlocked(): boolean {
    return !!this.participant?.ban;
  }

  get isMarked(): boolean {
    return !!this.participant?.flag;
  }

  get isNfcLinked(): boolean {
    return !!(this.participant?.shortTag || this.participant?.associatedNfc);
  }

  get statusPill(): StatusPill {
    if (this.isBlocked) return STATUS_PILLS.block;
    if (this.participant && this.participant.totScan > 0) return STATUS_PILLS.check;
    return STATUS_PILLS.wait;
  }

  get filteredTpls(): TplOption[] {
    const term = this.tplFilter.trim().toLowerCase();
    const list = this.availableTpls.filter(t => t._id !== this.participant?.tplId);
    if (!term) return list;
    return list.filter(t => t.name.toLowerCase().includes(term));
  }

  get canChangeTpl(): boolean {
    if (this.isBlocked || !this.participant) return false;
    return this.isFreeTicket && this.availableTpls.length > 0;
  }

  get isLoadingTpls(): boolean {
    if (this.isBlocked || !this.participant) return false;
    return this.isFreeTicket && this.availableTpls.length === 0;
  }

  private get isFreeTicket(): boolean {
    return isFreeTicket(this.participant);
  }

  trackById = (_: number, t: TplOption) => t._id;
  trackByIndex = (i: number) => i;

  historyIcon(entry: ParticipantHistoryEntry): string {
    switch (entry.type) {
      case 'in':    return 'fa-arrow-right-to-bracket';
      case 'out':   return 'fa-arrow-right-from-bracket';
      case 'mark':  return 'fa-flag';
      case 'block': return 'fa-ban';
    }
  }

  historyIconClass(entry: ParticipantHistoryEntry): string {
    switch (entry.type) {
      case 'in':    return '';
      case 'out':   return 'hist__icon--out';
      case 'mark':  return 'hist__icon--mark';
      case 'block': return 'hist__icon--block';
    }
  }

  onClose(): void {
    if (this.isDirty && !confirm('Modifications non enregistrées. Fermer quand même ?')) {
      return;
    }
    this.lastNameDraft = this.participant?.lastName ?? '';
    this.firstNameDraft = this.participant?.firstName ?? '';
    this.closed.emit();
  }

  onSave(): void {
    if (!this.participant || !this.isDirty) return;
    this.saved.emit({
      id: this.participant.id,
      lastName: this.lastNameDraft,
      firstName: this.firstNameDraft,
    });
    this.showToast('Modifications enregistrées', 'fa-circle-check');
  }

  onToggleTplDropdown(): void {
    if (!this.canChangeTpl) return;
    this.tplDropdownOpen = !this.tplDropdownOpen;
    this.tplFilter = '';
  }

  onSelectTpl(tpl: TplOption): void {
    if (!this.participant) return;
    this.tplChanged.emit({
      participantId: this.participant.id,
      newTplId: tpl._id,
      newTplName: tpl.name,
    });
    this.tplDropdownOpen = false;
    this.showToast(`Tarif changé : ${tpl.name}`, 'fa-tag');
  }

  onAssociateNfc(): void {
    if (!this.participant || !this.nfcDraft.trim() || this.isBlocked) return;
    this.nfcAssociated.emit({
      participantId: this.participant.id,
      tag: this.nfcDraft.trim(),
    });
    this.showToast(`Tag ${this.nfcDraft} associé`, 'fa-link');
  }

  onDisassociateNfc(): void {
    if (!this.participant || this.isBlocked) return;
    this.nfcDisassociated.emit({ participantId: this.participant.id });
    this.nfcDraft = '';
    this.showToast('Tag désassocié', 'fa-link-slash');
  }

  onIncrementScan(): void {
    if (!this.participant || this.isBlocked) return;
    if (this.participant.totScan >= this.participant.maxScan) return;
    this.scanIncremented.emit({ participantId: this.participant.id });
    this.showToast("Contrôle d'entrée enregistré", 'fa-check');
  }

  onDecrementScan(): void {
    if (!this.participant || this.isBlocked) return;
    if (this.participant.totScan <= 0) return;
    this.scanDecremented.emit({ participantId: this.participant.id });
    this.showToast('Contrôle annulé', 'fa-rotate-left');
  }

  onToggleFlag(): void {
    if (!this.participant) return;
    const next = !this.participant.flag;
    this.flagToggled.emit({ participantId: this.participant.id, flag: next });
    this.showToast(next ? 'Billet marqué' : 'Marqueur retiré', 'fa-flag');
  }

  onToggleBan(): void {
    if (!this.participant) return;
    const next = !this.participant.ban;
    this.banToggled.emit({ participantId: this.participant.id, ban: next });
    this.showToast(next ? 'Billet bloqué' : 'Billet débloqué', next ? 'fa-ban' : 'fa-lock-open');
  }

  copyId(): void {
    if (!this.participant) return;
    navigator.clipboard?.writeText(this.participant.id);
    this.showToast('Identifiant copié', 'fa-copy');
  }

  private showToast(msg: string, icon: string): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toast = { msg, icon };
    this.cdr.markForCheck();
    this.toastTimer = setTimeout(() => {
      this.toast = null;
      this.toastTimer = null;
      this.cdr.markForCheck();
    }, 2200);
  }
}
